import React from 'react';
import { Box, Button, Typography, CircularProgress, Divider } from '@mui/material';
import { Layers } from 'lucide-react';
import type { Planner } from './usePlanner';
import type { TargetTemplateInfo } from './types';
import { totalChanges } from './state';

export const ActionBar: React.FC<{
  planner: Planner;
  targets: TargetTemplateInfo[];
  isCopying: boolean;
  onCancel: () => void;
  onConfirm: () => Promise<void>;
}> = ({ planner, isCopying, onCancel, onConfirm }) => {
  const changes = totalChanges(planner.selection, planner.conflicts);
  const targetCount = Array.from(planner.selection.entries()).filter(([, s]) => s.size > 0).length;

  const bulkBtnStyle: React.CSSProperties = {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    color: '#007f80',
    fontSize: '0.7rem',
    fontWeight: 700,
    textTransform: 'uppercase',
    padding: '2px 0',
    textDecoration: 'none',
  };

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        borderTop: 1,
        borderColor: 'divider',
        bgcolor: 'background.paper',
        px: 3,
        py: 1.25,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
          <Box component="strong" sx={{ color: '#003366' }}>{changes}</Box>{' '}
          <Box component="span" sx={{ color: 'text.secondary' }}>
            change{changes === 1 ? '' : 's'} across
          </Box>{' '}
          <Box component="strong" sx={{ color: '#003366' }}>{targetCount}</Box>{' '}
          <Box component="span" sx={{ color: 'text.secondary' }}>
            target{targetCount === 1 ? '' : 's'}
          </Box>
        </Typography>
        <Divider orientation="vertical" flexItem />
        <button style={bulkBtnStyle} onClick={planner.selectAllVisible}>Select all visible</button>
        <button style={bulkBtnStyle} onClick={planner.clearVisibleSelection}>Clear visible</button>
        <button style={bulkBtnStyle} onClick={planner.expandAllGroups}>Expand all</button>
        <button style={bulkBtnStyle} onClick={planner.collapseAllGroups}>Collapse all</button>
      </Box>

      <Box sx={{ display: 'flex', gap: 1 }}>
        <Button
          onClick={onCancel}
          disabled={isCopying}
          size="small"
          sx={{
            textTransform: 'uppercase',
            fontWeight: 700,
            fontSize: '0.75rem',
            color: '#94a3b8',
            '&:hover': { bgcolor: 'transparent', color: '#64748b' },
          }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          size="small"
          onClick={onConfirm}
          disabled={isCopying || changes === 0}
          startIcon={isCopying ? <CircularProgress size={14} color="inherit" /> : <Layers size={16} />}
          sx={{
            textTransform: 'uppercase',
            fontWeight: 700,
            fontSize: '0.75rem',
            height: 40,
            px: 3,
            bgcolor: '#003366',
            '&:hover': { bgcolor: '#002244' },
            '&.Mui-disabled': { bgcolor: 'rgba(0,51,102,0.3)', color: 'white' },
            boxShadow: '0 4px 14px rgba(0,51,102,0.2)',
          }}
        >
          {isCopying ? 'Copying…' : `Review & Copy (${changes} change${changes === 1 ? '' : 's'})`}
        </Button>
      </Box>
    </Box>
  );
};
